import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Form, Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { EyeSlash, Eye } from "react-bootstrap-icons";
import {
  requestPasswordReset,
  clearResetPasswordError,
  clearResetPasswordSuccess,
  confirmPasswordReset,
  clearConfirmPasswordResetError,
  clearConfirmPasswordResetSuccess,
} from "./slice";
import VerificationCodeInput from "../../components/WebApp/VerificationCodeInput";
import PasswordRequirements from "../../components/WebApp/PasswordRequirements";
import "./resetPassword.css";
import { loginUser } from "../Login/slice";
import {
  validateEmail,
  validatePassword,
} from "../../../utils/helperFunctions/inputValidations";

const hasFieldError = (fieldName, error) => {
  if (!error || typeof error !== "object") return false;
  return error.hasOwnProperty(fieldName);
};

const getFieldError = (fieldName, error) => {
  if (!hasFieldError(fieldName, error)) return "";

  const fieldError = error[fieldName];
  if (Array.isArray(fieldError) && fieldError.length > 0) {
    return fieldError[0];
  }
  if (typeof fieldError === "string") {
    return fieldError;
  }
  return "";
};

const ResetPassword = () => {
  const dispatch = useDispatch();
  const {
    isResetLoading,
    resetPasswordSuccess,
    isConfirmPasswordResetLoading,
    confirmPasswordResetError,
  } = useSelector((state) => state.createAccount);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  useEffect(() => {
    // Clear errors on mount and unmount
    dispatch(clearResetPasswordError());
    dispatch(clearResetPasswordSuccess());
    dispatch(clearConfirmPasswordResetError());
    dispatch(clearConfirmPasswordResetSuccess());

    return () => {
      dispatch(clearResetPasswordError());
      dispatch(clearResetPasswordSuccess());
      dispatch(clearConfirmPasswordResetError());
      dispatch(clearConfirmPasswordResetSuccess());
    };
  }, [dispatch]);

  useEffect(() => {
    if (timeLeft > 0 && isResending) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setIsResending(false);
      setTimeLeft(60);
    }
  }, [timeLeft, isResending]);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordError(
      !value
        ? "Password is required"
        : !validatePassword(value)
          ? "Invalid password"
          : ""
    );
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setConfirmPasswordError(
      !value
        ? "Please confirm your password"
        : value !== newPassword
          ? "Passwords do not match"
          : ""
    );
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setIsResending(true);
    dispatch(requestPasswordReset({ email }));
  };

  const handleVerificationComplete = (code) => {
    setVerificationCode(code);
  };

  const handleResendCode = () => {
    setIsResending(true);
    dispatch(requestPasswordReset({ email }));
  };

  const handleResetSubmit = async () => {
    // For non-authenticated users, use the existing flow
    if (
      !passwordError &&
      !confirmPasswordError &&
      newPassword === confirmPassword
    ) {
      const resetData = {
        email,
        code: verificationCode,
        new_password: newPassword,
      };

      const resultAction = await dispatch(confirmPasswordReset(resetData));

      if (confirmPasswordReset.fulfilled.match(resultAction)) {
        // Password reset successful, proceed with auto-login
        const loginResult = await dispatch(
          loginUser({
            email,
            password: newPassword,
          })
        );

        if (loginUser.fulfilled.match(loginResult)) {
          navigate("/");
        }
      }
    }
  };

  const handleBack = () => {
    dispatch(clearConfirmPasswordResetError());
    dispatch(clearConfirmPasswordResetSuccess());
    navigate("/reset-password");
  };

  // Update field errors based on API response
  useEffect(() => {
    if (
      confirmPasswordResetError &&
      typeof confirmPasswordResetError === "object"
    ) {
      // Set new password error if it exists in API response
      if (hasFieldError("new_password", confirmPasswordResetError)) {
        setPasswordError(
          getFieldError("new_password", confirmPasswordResetError)
        );
      }
    }
  }, [confirmPasswordResetError]);

  return (
    <Container className="d-flex flex-column align-items-center py-5">
      <div className="w-100" style={{ maxWidth: "400px" }}>
        {resetPasswordSuccess ? (
          <div className="back-button">
            <Link to="/reset-password" onClick={handleBack}>
              ← Back
            </Link>
          </div>
        ) : null}

        <h1 className="page-title mb-4">Reset Password</h1>

        {!resetPasswordSuccess ? (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4" controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email"
                isInvalid={
                  !!emailError ||
                  hasFieldError("email", confirmPasswordResetError)
                }
                required
              />
              <Form.Control.Feedback type="invalid">
                {emailError ||
                  getFieldError("email", confirmPasswordResetError)}
              </Form.Control.Feedback>
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 primary-action-button"
              disabled={!email || !!emailError || isResetLoading}
            >
              {isResetLoading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="loading-button-spinner"
                />
              ) : (
                "Send Reset Code"
              )}
            </Button>
          </Form>
        ) : (
          <>
            <div className="text-center">
              <p className="email-sent-message">
                A 6-digit code has been sent to
                <br />
                <span className="user-email">{email}</span>
              </p>

              <p className="verification-instruction">
                Please enter the verification code below
              </p>
            </div>
            <VerificationCodeInput
              onComplete={handleVerificationComplete}
              error={confirmPasswordResetError}
              isLoading={isResetLoading}
              showResend={true}
              onResend={handleResendCode}
              isResending={isResending}
              timeLeft={timeLeft}
            />
            <hr className="divider" />

            <Form className="mt-4">
              <Form.Group className="mb-3">
                <Form.Label>New Password*</Form.Label>
                <div className="password-input-wrapper">
                  <Form.Control
                    type="text"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    isInvalid={
                      !!passwordError ||
                      hasFieldError("new_password", confirmPasswordResetError)
                    }
                    style={{
                      WebkitTextSecurity: showPassword ? "none" : "disc",
                      MozTextSecurity: showPassword ? "none" : "disc",
                      textSecurity: showPassword ? "none" : "disc",
                    }}
                    required
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <Eye size={16} /> : <EyeSlash size={16} />}
                  </span>
                </div>
                <Form.Control.Feedback type="invalid">
                  {passwordError || getFieldError("new_password")}
                </Form.Control.Feedback>
                {newPassword ? (
                  <PasswordRequirements password={newPassword} />
                ) : null}
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirm New Password*</Form.Label>
                <div className="password-input-wrapper">
                  <Form.Control
                    type="text"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    isInvalid={!!confirmPasswordError}
                    style={{
                      WebkitTextSecurity: showConfirmPassword ? "none" : "disc",
                      MozTextSecurity: showConfirmPassword ? "none" : "disc",
                      textSecurity: showConfirmPassword ? "none" : "disc",
                    }}
                    required
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    {showConfirmPassword ? (
                      <Eye size={16} />
                    ) : (
                      <EyeSlash size={16} />
                    )}
                  </span>
                </div>
                <Form.Control.Feedback type="invalid">
                  {confirmPasswordError}
                </Form.Control.Feedback>
              </Form.Group>

              <Button
                variant="primary"
                className="w-100 primary-action-button"
                onClick={handleResetSubmit}
                disabled={
                  !newPassword ||
                  !confirmPassword ||
                  !!passwordError ||
                  !!confirmPasswordError ||
                  !verificationCode ||
                  isConfirmPasswordResetLoading
                }
              >
                {isConfirmPasswordResetLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="loading-button-spinner"
                  />
                ) : (
                  "Confirm Reset"
                )}
              </Button>
            </Form>
          </>
        )}

        <div className="text-center mt-3 text-small">
          Remembered your password?{" "}
          <Link to="/login" className="text-button-link">
            Login
          </Link>
        </div>
      </div>
    </Container>
  );
};

export default ResetPassword;
